import React, { useMemo, useState } from 'react'
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import { useQuery } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router-dom'
import { GET_PROJECTS } from '../../../entities/prompts/queries'
import ProjectPromptEditor from './ProjectPromptEditor'
import { useSystemPromptsStyles } from './SystemPrompts.styles'
import { usePagination } from '../../../hooks'
import EmptyState from '../components/EmptyState'
import AlertMessage from '../../../components/AlertMessage'

const PROJECTS_PER_PAGE = 15

function ProjectPrompts() {
  const classes = useSystemPromptsStyles()
  const { data, loading, error } = useQuery(GET_PROJECTS)
  const navigate = useNavigate()
  const location = useLocation()
  const [alert, setAlert] = useState({ isOpen: false, message: '', severity: '' })

  const searchParams = new URLSearchParams(location.search)
  const searchTerm = searchParams.get('q') || ''

  const processedProjects = useMemo(() => {
    const projects = data?.getProjects || []
    const sorted = [...projects].sort((a, b) => a.name.localeCompare(b.name))
    return sorted.filter(project => project.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [data?.getProjects, searchTerm])

  const {
    page,
    pageCount,
    paginatedItems: paginatedProjects,
    onPageChange,
    onResetPage,
  } = usePagination(processedProjects, { itemsPerPage: PROJECTS_PER_PAGE })

  const handleSearchChange = event => {
    const query = event.target.value
    const params = new URLSearchParams(location.search)

    if (query) params.set('q', query)
    else params.delete('q')

    navigate(`${location.pathname}?${params}`, { replace: true })
    onResetPage()
  }

  const clearSearchHandler = () => {
    const params = new URLSearchParams(location.search)
    params.delete('q')
    navigate(`${location.pathname}?${params}`, { replace: true })
    onResetPage()
  }

  const handleCloseAlert = () => {
    setAlert({ ...alert, isOpen: false })
  }

  const handleSuccess = message => {
    setAlert({ isOpen: true, message, severity: 'success' })
  }

  const handleError = message => {
    setAlert({ isOpen: true, message, severity: 'error' })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Typography color="error">Error loading projects: {error.message}</Typography>
  }

  return (
    <div className={classes.root}>
      <Typography variant="h2" component="h6" style={{ marginBottom: '24px' }}>
        Project Prompts
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search projects..."
        value={searchTerm}
        onChange={handleSearchChange}
        style={{ marginBottom: '24px' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {paginatedProjects.length === 0 ? (
        <EmptyState
          isSearching={searchTerm.length > 0}
          searchTerm={searchTerm}
          onClearSearch={clearSearchHandler}
        />
      ) : (
        <>
          {paginatedProjects.map(project => (
            <Accordion key={project._id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{project.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ProjectPromptEditor
                  project={project}
                  onSuccess={handleSuccess}
                  onError={handleError}
                />
              </AccordionDetails>
            </Accordion>
          ))}

          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(event, value) => onPageChange(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <AlertMessage
        isOpen={alert.isOpen}
        handleClose={handleCloseAlert}
        message={alert.message}
        severity={alert.severity}
      />
    </div>
  )
}

export default ProjectPrompts
